import React, { useContext, lazy, Suspense, useMemo, useState } from 'react'
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import AuthContext from '../store/AuthContext'
import Profile from '../Pages/Profile';
import { toast } from 'react-toastify';
import { BASE_URL_API } from '../References/urls';
import { FaSpinner } from 'react-icons/fa';
import loginStyles from '../Styles/Pages/Login.module.css';

export const PageLoader = () => (
    <div
        style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
        }}
    >
        <div
            style={{
                width: 24,
                height: 24,
                border: "2px solid #e5e5e5",
                borderTop: "2px solid #333",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
            }}
        />
        <style>
            {`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}
        </style>
    </div>
);

const ProtectedRoute = ({ children }) => {
    const { isLoggedIn } = useContext(AuthContext);
    const location = useLocation();
    if (!isLoggedIn) {
        if (localStorage.getItem("logoutJustHappened") === "1") {
            localStorage.removeItem("logoutJustHappened");
            return <Navigate to="/" replace />;
        }
        const redirectPath = `${location.pathname}${location.search || ""}`;
        localStorage.setItem("postLoginRedirect", redirectPath);
        localStorage.setItem("postLoginRedirectSetAt", String(Date.now()));
        return <Navigate to="/login" replace state={{ from: location }} />;
    }
    return children;
};

const ProfileCompletionModal = () => {
    const authCtx = useContext(AuthContext);
    const navigate = useNavigate();
    const [registrationForm, setRegistrationForm] = useState({
        fullName: "",
        dateOfBirth: "",
        gender: "",
    });
    const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

    const { minDob, maxDob } = useMemo(() => {
        const formatDateForInput = (date) => {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, "0");
            const d = String(date.getDate()).padStart(2, "0");
            return `${y}-${m}-${d}`;
        };
        const today = new Date();
        return {
            minDob: formatDateForInput(
                new Date(today.getFullYear() - 60, today.getMonth(), today.getDate())
            ),
            maxDob: formatDateForInput(
                new Date(today.getFullYear() - 10, today.getMonth(), today.getDate())
            ),
        };
    }, []);

    const getPostLoginRedirect = () => {
        const storedPath = localStorage.getItem("postLoginRedirect");
        const path = storedPath || "/";
        if (path.startsWith("/login")) return "/";
        return path;
    };

    const handleRegistrationFormChange = (e) => {
        const { name, value } = e.target;
        setRegistrationForm((prev) => ({ ...prev, [name]: value }));
    };

    const updateUserProfile = async () => {
        const tempId = authCtx.tempUserId || sessionStorage.getItem("tempUserId");
        if (!tempId) {
            authCtx.clearPendingProfileCompletion();
            toast.error("Session expired. Please login again.");
            return;
        }

        setIsUpdatingProfile(true);
        try {
            const response = await fetch(BASE_URL_API + "/updateUserProfile", {
                method: "POST",
                body: JSON.stringify({
                    userIdentifier: tempId,
                    fullName: registrationForm.fullName.trim(),
                    dateOfBirth: registrationForm.dateOfBirth,
                    gender: registrationForm.gender,
                }),
            });

            if (!response.ok) {
                toast.error("Failed to update profile.");
                return;
            }

            const respo = await response.json();
            if (respo.status !== "success") {
                toast.error(respo.message || "Failed to update profile.");
                return;
            }

            if (!respo.authToken) {
                toast.error("Login token missing. Please login again.");
                return;
            }

            authCtx.login(respo.authToken, respo.userId || tempId);
            authCtx.clearPendingProfileCompletion();

            const redirectPath = getPostLoginRedirect();
            localStorage.removeItem("postLoginRedirect");
            navigate(redirectPath, { replace: true });
            toast.success("Profile updated successfully!");
        } catch (error) {
            console.error(error);
            toast.error("Network error");
        } finally {
            setIsUpdatingProfile(false);
        }
    };

    if (authCtx.isLoggedIn) return null;
    if (!authCtx.pendingProfileCompletion) return null;
    if (!authCtx.tempUserId) return null;

    return (
        <div className={loginStyles.registrationOverlay}>
            <div className={loginStyles.registrationPopup}>
                <div className={loginStyles.registrationHeader}>
                    <h3>Complete Your Profile</h3>
                </div>
                <div className={loginStyles.registrationBody}>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            if (!e.currentTarget.checkValidity()) {
                                e.currentTarget.reportValidity();
                                return;
                            }
                            if (
                                registrationForm.dateOfBirth < minDob ||
                                registrationForm.dateOfBirth > maxDob
                            ) {
                                toast.error("DOB must be between 10 and 60 years.");
                                return;
                            }
                            if (!isUpdatingProfile) updateUserProfile();
                        }}
                    >
                        <div className={loginStyles.registrationInputGroup}>
                            <label>Full Name *</label>
                            <input
                                type="text"
                                name="fullName"
                                value={registrationForm.fullName}
                                onChange={handleRegistrationFormChange}
                                placeholder="Enter your full name"
                                required
                                minLength={2}
                                maxLength={80}
                            />
                        </div>
                        <div className={loginStyles.registrationInputGroup}>
                            <label>Date of Birth *</label>
                            <input
                                type="date"
                                name="dateOfBirth"
                                value={registrationForm.dateOfBirth}
                                onChange={handleRegistrationFormChange}
                                min={minDob}
                                max={maxDob}
                                required
                            />
                        </div>
                        <div className={loginStyles.registrationInputGroup}>
                            <label>Gender *</label>
                            <select
                                name="gender"
                                value={registrationForm.gender}
                                onChange={handleRegistrationFormChange}
                                required
                            >
                                <option value="">Select Gender</option>
                                <option value="MALE">Male</option>
                                <option value="FEMALE">Female</option>
                                <option value="LGBTQ+">LGBTQ+</option>
                            </select>
                        </div>
                        <button
                            type="submit"
                            className={loginStyles.registrationSubmitBtn}
                            disabled={isUpdatingProfile}
                        >
                            {isUpdatingProfile ? (
                                <>
                                    <FaSpinner className={loginStyles.registrationSpinner} /> Updating...
                                </>
                            ) : (
                                "Complete Registration"
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};


// CRITICAL: Each lazy import creates a separate chunk
// Use webpackChunkName to name them properly

// Landing pages - highest priority
const FindSalonJobs = lazy(() =>
    import(/* webpackChunkName: "find-salon-jobs" */ '../Pages/LandingPages/FindSalonJobs')
)
const Home = lazy(() =>
    import(/* webpackChunkName: "home" */ '../Pages/LandingPages/Home')
)

// Auth pages
const Login = lazy(() =>
    import(/* webpackChunkName: "login" */ '../Pages/Login')
)
const LoginForWorkshop = lazy(() =>
    import(/* webpackChunkName: "login-workshop" */ '../Pages/Authentication/LoginForWorkshop')
)

// Static pages - can be grouped
const SLCs = lazy(() =>
    import(/* webpackChunkName: "static-pages" */ '../Pages/SLCs')
)
const About = lazy(() =>
    import(/* webpackChunkName: "static-pages" */ '../Pages/About')
)
const PrivacyPolicy = lazy(() =>
    import(/* webpackChunkName: "static-pages" */ '../Pages/PrivacyPolicy')
)
const TermsOfUse = lazy(() =>
    import(/* webpackChunkName: "static-pages" */ '../Pages/TermsOfUse')
)
const RefundPolicy = lazy(() =>
    import(/* webpackChunkName: "static-pages" */ '../Pages/RefundPolicy')
)
const OurPresence = lazy(() =>
    import(/* webpackChunkName: "static-pages" */ '../Pages/OurPresence')
)

// Course pages
const Courses = lazy(() =>
    import(/* webpackChunkName: "courses" */ '../Pages/Courses')
)
const PremiumCategory = lazy(() =>
    import(/* webpackChunkName: "courses" */ '../Courses/PremiumCategory')
)
const PremiumAsset = lazy(() =>
    import(/* webpackChunkName: "courses" */ '../Courses/PremiumAsset')
)
const SearchAmongCourses = lazy(() =>
    import(/* webpackChunkName: "courses" */ '../Courses/SearchAmongCourses')
)

// Jobs pages
const JobsListing = lazy(() =>
    import(/* webpackChunkName: "jobs" */ '../Jobs/JobsListing')
)
const EachJob = lazy(() =>
    import(/* webpackChunkName: "jobs" */ '../Jobs/EachJob')
)
const JobsListingCategorised = lazy(() =>
    import(/* webpackChunkName: "jobs" */ '../Pages/LandingPages/JobsListingCategorised')
)

// Workshop pages
const Workshops = lazy(() =>
    import(/* webpackChunkName: "workshops" */ '../Pages/Workshops')
)
const PremiumAssestsWorkshops = lazy(() =>
    import(/* webpackChunkName: "workshops" */ '../Pages/Workshops/PremiumAssestsWorkshops')
)
const RegisterForWorkshop = lazy(() =>
    import(/* webpackChunkName: "workshops" */ '../Pages/Workshops/RegisterForWorkshop')
)
const RegisterWorkshopPaymentStatus = lazy(() =>
    import(/* webpackChunkName: "workshops" */ '../Pages/Workshops/RegisterWorkshopPaymentStatus')
)

// Certificates - separate chunk as they're rarely accessed
const Verify = lazy(() =>
    import(/* webpackChunkName: "certificates" */ '../Certificates/Verify')
)
const GCWCertificateAdmittance = lazy(() =>
    import(/* webpackChunkName: "certificates" */ '../Components/Workshops/GCWCertificateAdmittance')
)
const GCWCertificateAssessmentCover = lazy(() =>
    import(/* webpackChunkName: "certificates" */ '../Components/Workshops/GCWCertificateAssessmentCover')
)
const GCWCertificateAssessmentExam = lazy(() =>
    import(/* webpackChunkName: "certificates" */ '../Components/Workshops/GCWCertificateAssessmentExam')
)
const GCWCertificateAssessmentResult = lazy(() =>
    import(/* webpackChunkName: "certificates" */ '../Components/Workshops/GCWCertificateAssessmentResult')
)
const GCWCertificate = lazy(() =>
    import(/* webpackChunkName: "certificates" */ '../Components/Workshops/GCWCertificate')
)

// Community & Training
const Trainings = lazy(() =>
    import(/* webpackChunkName: "training" */ '../Pages/Trainings')
)
const Community = lazy(() =>
    import(/* webpackChunkName: "community" */ '../Pages/Community')
)
const TrainYourStaff = lazy(() =>
    import(/* webpackChunkName: "training" */ '../Pages/TrainYourStaff')
)

// Business pages
const HireSalonStaffHome = lazy(() =>
    import(/* webpackChunkName: "business" */ '../Pages/ForBusinesses/HireSalonStaffHome')
)
const HireSalonStaffQuickly = lazy(() =>
    import(/* webpackChunkName: "business" */ '../Pages/ForBusinesses/HireSalonStaffQuickly')
)
const SubscriptionsPricing = lazy(() =>
    import(/* webpackChunkName: "business" */ '../Pages/ForBusinesses/SubscriptionsPricing')
)
const ForSalon = lazy(() =>
    import(/* webpackChunkName: "business" */ '../Pages/LandingPages/ForSalon')
)
const Resume = lazy(() =>
    import(/* webpackChunkName: "business" */ '../Pages/LandingPages/Resume')
)

// Classroom pages
const ClassroomHome = lazy(() =>
    import(/* webpackChunkName: "classroom" */ '../Pages/Classroom/ClassroomHome')
)
const MakeupPromotionalLandingPage = lazy(() =>
    import(/* webpackChunkName: "classroom" */ '../Pages/Classroom/PromotionalLandingPages/MakeupPromotionalLandingPage')
)
const CHCourseEnquiryForm = lazy(() =>
    import(/* webpackChunkName: "classroom" */ '../Pages/Classroom/CHCourseEnquiryForm')
)
const HairPLP = lazy(() =>
    import(/* webpackChunkName: "classroom" */ '../Pages/Classroom/PromotionalLandingPages/HairPLP')
)

// Brand partners & Recruitment
const BPProducts = lazy(() =>
    import(/* webpackChunkName: "brand-partners" */ '../Pages/BrandPartners/BPProducts')
)
const InterviewDetails = lazy(() =>
    import(/* webpackChunkName: "recruitment" */ '../Pages/Recruitment/InterviewDetails')
)

const MainNavigator = () => {
    const authCtx = useContext(AuthContext)
    const isLoggedIn = authCtx.isLoggedIn

    return (
        <Suspense fallback={<PageLoader />}>
            <ProfileCompletionModal />
            <Routes>
                {/* <Route path="/login" element={isLoggedIn ? <Login /> : <LoginForWorkshop />} /> */}
                <Route path="/login" element={isLoggedIn ? <Navigate to="/" replace /> : <Login />} />
                <Route path="/loginforworkshop" element={<LoginForWorkshop />} />
                <Route
                    path="/profile"
                    element={
                        <ProtectedRoute>
                            <Profile />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/profile/appliedjobs"
                    element={
                        <ProtectedRoute>
                            <Profile />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/profile/savedjobs"
                    element={
                        <ProtectedRoute>
                            <Profile />
                        </ProtectedRoute>
                    }
                />

                {/* Landing pages */}
                <Route path="*" element={<FindSalonJobs />} />
                <Route path="/" element={<FindSalonJobs />} />
                <Route path="/home" element={<Home />} />
                <Route path="/find-salon-jobs" element={<FindSalonJobs />} />

                {/* Static pages */}
                <Route path="/slc" element={<SLCs />} />
                <Route path="/about" element={<About />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-use" element={<TermsOfUse />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/our-presence" element={<OurPresence />} />

                {/* Courses */}
                <Route path="/courses" element={<Courses />} />
                <Route path="/online-courses/:category" element={<PremiumCategory />} />
                <Route path="/online-course/:category/:identifier" element={<PremiumAsset />} />
                <Route path="/online-courses/search" element={<SearchAmongCourses />} />

                {/* Jobs */}
                <Route path="/jobs" element={<JobsListing />} />
                <Route path="/job/:route" element={<EachJob />} />
                <Route path="/job/:route/:trackIdentifier" element={<EachJob />} />
                <Route path="/:routeIdentifier" element={<JobsListingCategorised />} />

                {/* Certificates */}
                <Route path="/certificate/verify/:certificateIdentifier" element={<Verify />} />

                {/* Workshops */}
                <Route path="/workshops" element={<Workshops />} />
                <Route path="/workshop/:identifier" element={<PremiumAssestsWorkshops />} />
                <Route path="/workshop/:workshopIdentifier/register" element={<RegisterForWorkshop />} />
                {isLoggedIn && (
                    <Route path="/workshop/:workshopIdentifier/payment-status" element={<RegisterWorkshopPaymentStatus />} />
                )}
                <Route path="/workshop/certificate/admittance/:identifier" element={<GCWCertificateAdmittance />} />
                <Route path="/workshop/certificate/assessment/:identifier/cover" element={<GCWCertificateAssessmentCover />} />
                <Route path="/workshop/certificate/assessment/:identifier/exam" element={<GCWCertificateAssessmentExam />} />
                <Route path="/workshop/certificate/assessment/:identifier/result" element={<GCWCertificateAssessmentResult />} />
                <Route path="/workshop/certificate/:identifier" element={<GCWCertificate />} />

                {/* Training & Community */}
                <Route path="/training" element={<Trainings />} />
                <Route path="/community" element={<Community />} />
                <Route path="/train-your-salon-staff" element={<TrainYourStaff />} />

                {/* Business pages */}
                <Route path="/hire-salon-staff" element={<HireSalonStaffHome />} />
                <Route path="/hire-salon-staff/quickly" element={<HireSalonStaffQuickly />} />
                <Route path="/hire-salon-staff/pricing" element={<SubscriptionsPricing />} />
                <Route path="/solutions-for-businesses" element={<ForSalon />} />
                <Route path="/get-my-salon-resume" element={<Resume />} />

                {/* Classroom */}
                <Route path="/classroom-courses" element={<ClassroomHome />} />
                <Route path="/classroom-courses/make-up-courses" element={<MakeupPromotionalLandingPage />} />
                <Route path="/classroom-courses/:categoryIdentifier/enroll-now" element={<CHCourseEnquiryForm />} />
                <Route path="/classroom-courses/hair-dresser-courses" element={<HairPLP />} />

                {/* Brand partners & Recruitment */}
                <Route path="/brand-partners/:category" element={<BPProducts />} />
                <Route path="/recruitment/interview-details/:identifier" element={<InterviewDetails />} />
            </Routes>
        </Suspense>
    )
}

export default MainNavigator
